/** Shared image asset (signature stroke or stamp). */
export type SignatureAsset = {
  storageKey: string;
  fileName: string;
  mimeType: string;
  size: number;
};

/**
 * Shared signature value used across e-forms, crew, MGA, etc.
 * Root fields are the drawn/uploaded signature; optional `stamp` pairs an official stamp.
 */
export type SignatureValue = SignatureAsset & {
  signedAt?: string;
  signerName?: string;
  /** How the signature image was captured. */
  source?: 'draw' | 'upload';
  subjectType?: 'user' | 'crew' | 'external';
  subjectId?: number;
  /**
   * Optional stamp / seal image shown behind the signature on the same canvas
   * (stamp first, then sign over it — like paper).
   */
  stamp?: SignatureAsset;
};

export function isSignatureAsset(value: unknown): value is SignatureAsset {
  if (!value || typeof value !== 'object') return false;
  const o = value as Record<string, unknown>;
  return (
    typeof o.storageKey === 'string' &&
    o.storageKey.length > 0 &&
    typeof o.fileName === 'string' &&
    o.fileName.length > 0
  );
}

export function isSignatureValue(value: unknown): value is SignatureValue {
  if (!isSignatureAsset(value)) return false;
  const o = value as Record<string, unknown>;
  if (o.stamp != null && !isSignatureAsset(o.stamp)) return false;
  return true;
}

/** Legacy e-forms stored typed names as plain strings. */
export function coerceSignatureValue(value: unknown): SignatureValue | null {
  if (isSignatureValue(value)) return value;
  return null;
}

export function signatureAssetFromUpload(meta: {
  storageKey: string;
  fileName: string;
  mimeType?: string;
  size: number;
}): SignatureAsset {
  return {
    storageKey: meta.storageKey,
    fileName: meta.fileName,
    mimeType: meta.mimeType || 'image/png',
    size: meta.size,
  };
}
